import React, { useEffect, useState } from 'react';
import Modal from '../../../shared/components/ui/Modal';
import Button from '../../../shared/components/ui/Button';
import { TacticsImageStatus } from '../../../shared/services/tacticsImagesService';

interface SaveTacticsModalProps {
  isOpen: boolean;
  initialName: string;
  isSaving: boolean;
  isEditingExisting?: boolean;
  tacticsImageStatus?: TacticsImageStatus;
  onClose: () => void;
  onSave: (name: string, action: 'update' | 'create') => void;
}

const SaveTacticsModal: React.FC<SaveTacticsModalProps> = ({
  isOpen,
  initialName,
  isSaving,
  isEditingExisting = false,
  tacticsImageStatus,
  onClose,
  onSave,
}) => {
  const [name, setName] = useState(initialName);
  const [action, setAction] = useState<'update' | 'create'>('create');

  useEffect(() => {
    if (isOpen) {
      // Only set name when modal first opens, not on every initialName change
      // This prevents the name from updating while user is typing
      setName(initialName);
      // Default to 'update' if editing existing, but only if status allows it
      if (isEditingExisting && tacticsImageStatus === 'saved') {
        setAction('update');
      } else {
        setAction('create');
      }
    }
  }, [isOpen, isEditingExisting, tacticsImageStatus]); // Removed initialName from dependencies

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSave(name.trim(), action);
  };

  // Check if update is disabled (only 'saved' status can be updated)
  const isUpdateDisabled = !isEditingExisting || 
    !tacticsImageStatus || 
    tacticsImageStatus === 'uploaded';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Save Tactics Configuration" className="max-w-md">
      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        <div>
          <label htmlFor="tactics-name" className="block text-sm font-medium text-gray-700 mb-2">
            Configuration name
          </label>
          <input
            id="tactics-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter configuration name"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            maxLength={80}
            autoFocus
          />
          <p className="text-xs text-gray-400 mt-1">
            Choose a descriptive name (e.g., &ldquo;First-half passing patterns&rdquo;).
          </p>
        </div>

        {isEditingExisting && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Save option
            </label>
            <div className="space-y-2">
              <label className={`flex items-start p-3 border rounded-md cursor-pointer transition-colors ${
                action === 'update' && !isUpdateDisabled
                  ? 'border-blue-500 bg-blue-50'
                  : action === 'update' && isUpdateDisabled
                  ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
                  : 'border-gray-200 hover:border-gray-300'
              }`}>
                <input
                  type="radio"
                  name="save-action"
                  value="update"
                  checked={action === 'update'}
                  onChange={() => !isUpdateDisabled && setAction('update')}
                  disabled={isUpdateDisabled}
                  className="mt-0.5 mr-3"
                />
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">
                    Update existing
                  </div>
                  {isUpdateDisabled && (
                    <div className="text-xs text-gray-500 mt-1">
                      Cannot update uploaded tactics images
                    </div>
                  )}
                </div>
              </label>
              
              <label className={`flex items-start p-3 border rounded-md cursor-pointer transition-colors ${
                action === 'create'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}>
                <input
                  type="radio"
                  name="save-action"
                  value="create"
                  checked={action === 'create'}
                  onChange={() => setAction('create')}
                  className="mt-0.5 mr-3"
                />
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">
                    Create new
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Save as a new configuration
                  </div>
                </div>
              </label>
            </div>
          </div>
        )}

        <div className="flex justify-end space-x-3">
          <Button type="button" variant="ghost" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={isSaving || !name.trim()}>
            {isSaving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default SaveTacticsModal;

